/**
 * SidebarVisualizer — Compact visualizer widget that lives in the DJLayout sidebar.
 *
 * Shows a small preview canvas with minimal controls:
 *   - Start/stop audio capture (system audio or mic)
 *   - Play/pause the visualizer
 *   - Next/previous/random visualization
 *   - Open the full control modal
 *   - Pop out projection window
 *
 * The full VisualizerModal handles advanced settings (pool, modes, per-viz settings, etc.)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Play, Pause, SkipForward, SkipBack, Shuffle, Monitor, Mic, MonitorOff,
  Maximize2, Settings2, Activity, Eye, EyeOff, Snowflake, Save
} from 'lucide-react';
import { VisualizationEngine, DEFAULT_VISUALIZER_STATE, saveVisualizerDefaults, savePreferredAudioSource, loadPreferredAudioSource } from '../VisualizationEngine';
import { getAllVisualizationsWithCustom } from '../visualizations';
import { VisualizerState } from '../types';
import VisualizerModal from './VisualizerModal';
import toast from 'react-hot-toast';

export default function SidebarVisualizer() {
  const engineRef = useRef<VisualizationEngine | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const projectionWindowRef = useRef<Window | null>(null);

  const [state, setState] = useState<VisualizerState>(DEFAULT_VISUALIZER_STATE);
  const [audioStatus, setAudioStatus] = useState<'none' | 'system' | 'microphone'>('none');
  const [modalOpen, setModalOpen] = useState(false);
  const [projectionOpen, setProjectionOpen] = useState(false);

  // ── Initialize engine (singleton for the app lifetime) ──────────────────
  useEffect(() => {
    const engine = new VisualizationEngine();
    engineRef.current = engine;

    engine.setStateChangeCallback((newState) => {
      setState({ ...newState });
    });

    return () => {
      engine.destroy();
      engineRef.current = null;
    };
  }, []);

  // ── Attach canvas ───────────────────────────────────────────────────────
  useEffect(() => {
    const engine = engineRef.current;
    const canvas = canvasRef.current;
    if (!engine || !canvas) return;

    engine.attachCanvas(canvas);

    const resize = () => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      engine.handleResize(rect.width, Math.max(100, rect.height));
    };
    resize();

    const observer = new ResizeObserver(resize);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // ── Keyboard shortcuts (global when visualizer is running) ──────────────
  useEffect(() => {
    if (!state.running) return;

    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLSelectElement) return;
      const engine = engineRef.current;
      if (!engine) return;

      switch (e.key) {
        case 'ArrowRight': case 'n': e.preventDefault(); engine.nextVisualization(); break;
        case 'ArrowLeft': case 'p': e.preventDefault(); engine.prevVisualization(); break;
        case 'r': e.preventDefault(); engine.randomVisualization(); break;
        case 'b': e.preventDefault(); engine.updateState({ blackout: !state.blackout }); break;
        case 'f': e.preventDefault(); engine.updateState({ frozen: !state.frozen }); break;
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [state.running, state.blackout, state.frozen]);

  // ── Actions ─────────────────────────────────────────────────────────────

  const startAudio = useCallback(async (source: 'system' | 'microphone') => {
    const engine = engineRef.current;
    if (!engine) return;
    try {
      await engine.startAudio(source);
      setAudioStatus(source);
      savePreferredAudioSource(source);
      toast.success(source === 'system' ? 'System audio connected' : 'Microphone connected');
      if (!state.running) engine.start();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to start audio capture');
    }
  }, [state.running]);

  const stopAudio = useCallback(() => {
    engineRef.current?.audioManager.stop();
    setAudioStatus('none');
  }, []);

  const toggleRunning = useCallback(() => {
    const engine = engineRef.current;
    if (!engine) return;
    if (state.running) {
      engine.stop();
      setAudioStatus('none');
    } else {
      engine.start();
    }
  }, [state.running]);

  const openProjectionWindow = useCallback(() => {
    if (projectionWindowRef.current && !projectionWindowRef.current.closed) {
      projectionWindowRef.current.focus();
      return;
    }
    // popup=yes tells browsers to open a minimal chromeless window
    const win = window.open(
      '/visualizer/projection',
      'votebeats-projection',
      'popup=yes,width=1280,height=720'
    );
    projectionWindowRef.current = win;
    setProjectionOpen(true);

    // Poll for window close (no reliable cross-origin close event)
    const poll = setInterval(() => {
      if (!win || win.closed) {
        clearInterval(poll);
        projectionWindowRef.current = null;
        setProjectionOpen(false);
      }
    }, 1000);
  }, []);

  const closeProjectionWindow = useCallback(() => {
    if (projectionWindowRef.current && !projectionWindowRef.current.closed) {
      projectionWindowRef.current.close();
    }
    projectionWindowRef.current = null;
    setProjectionOpen(false);
  }, []);

  const activeViz = getAllVisualizationsWithCustom().find(v => v.id === state.activeVisualizationId);

  return (
    <>
      <div className="px-3 pb-3">
        <div className="bg-gray-800/60 rounded-lg overflow-hidden border border-gray-700/50">
          {/* Compact header */}
          <div className="flex items-center justify-between px-2.5 py-1.5">
            <div className="flex items-center gap-1.5">
              <Activity className="w-3.5 h-3.5 text-purple-400" />
              <span className="text-[11px] font-semibold text-gray-200">Visualizer</span>
              {state.running && (
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
              )}
            </div>
            <div className="flex items-center gap-0.5">
              {audioStatus !== 'none' && (
                <span className="text-[9px] text-purple-400 font-medium mr-1">
                  {audioStatus === 'system' ? 'SYS' : 'MIC'}
                </span>
              )}
            </div>
          </div>

          {/* Preview canvas */}
          <div ref={containerRef} className="relative w-full h-24 bg-black">
            <canvas
              ref={canvasRef}
              className="w-full h-full block"
            />
            {!state.running && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 gap-1.5">
                <span className="text-[10px] text-gray-500">Audio visualizer</span>
                <div className="flex gap-1">
                  <button
                    onClick={() => startAudio('system')}
                    className="flex items-center gap-1 px-2 py-1 rounded bg-purple-600/80 hover:bg-purple-600 text-white text-[10px] font-medium transition-colors"
                  >
                    <Monitor className="w-3 h-3" /> System
                  </button>
                  <button
                    onClick={() => startAudio('microphone')}
                    className="flex items-center gap-1 px-2 py-1 rounded bg-gray-600/80 hover:bg-gray-600 text-white text-[10px] font-medium transition-colors"
                  >
                    <Mic className="w-3 h-3" /> Mic
                  </button>
                </div>
              </div>
            )}
            {state.showFps && state.running && (
              <span className="absolute top-0.5 right-1 text-[9px] font-mono text-green-400/70">
                {engineRef.current?.getFps() ?? 0}
              </span>
            )}
          </div>

          {/* Controls row */}
          <div className="flex items-center justify-between px-1.5 py-1">
            {/* Left: playback controls */}
            <div className="flex items-center gap-0.5">
              <button
                onClick={() => engineRef.current?.prevVisualization()}
                disabled={!state.running}
                className="p-1 rounded text-gray-400 hover:text-white hover:bg-gray-700 disabled:opacity-20 transition-colors"
                title="Previous (P)"
              >
                <SkipBack className="w-3 h-3" />
              </button>

              {state.running ? (
                <button
                  onClick={toggleRunning}
                  className="p-1 rounded text-red-400 hover:text-red-300 hover:bg-gray-700 transition-colors"
                  title="Stop"
                >
                  <Pause className="w-3.5 h-3.5" />
                </button>
              ) : (
                <button
                  onClick={() => {
                    if (audioStatus !== 'none') {
                      engineRef.current?.start();
                    } else {
                      startAudio(loadPreferredAudioSource());
                    }
                  }}
                  className="p-1 rounded text-green-400 hover:text-green-300 hover:bg-gray-700 transition-colors"
                  title="Start"
                >
                  <Play className="w-3.5 h-3.5" />
                </button>
              )}

              <button
                onClick={() => engineRef.current?.nextVisualization()}
                disabled={!state.running}
                className="p-1 rounded text-gray-400 hover:text-white hover:bg-gray-700 disabled:opacity-20 transition-colors"
                title="Next (N)"
              >
                <SkipForward className="w-3 h-3" />
              </button>

              <button
                onClick={() => engineRef.current?.randomVisualization()}
                disabled={!state.running}
                className="p-1 rounded text-gray-400 hover:text-white hover:bg-gray-700 disabled:opacity-20 transition-colors"
                title="Random (R)"
              >
                <Shuffle className="w-3 h-3" />
              </button>
            </div>

            {/* Right: quick toggles + modal + projection */}
            <div className="flex items-center gap-0.5">
              <button
                onClick={() => engineRef.current?.updateState({ blackout: !state.blackout })}
                disabled={!state.running}
                className={`p-1 rounded transition-colors disabled:opacity-20 ${state.blackout ? 'text-yellow-400 bg-gray-700' : 'text-gray-400 hover:text-white hover:bg-gray-700'}`}
                title="Blackout (B)"
              >
                {state.blackout ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
              </button>

              <button
                onClick={() => engineRef.current?.updateState({ frozen: !state.frozen })}
                disabled={!state.running}
                className={`p-1 rounded transition-colors disabled:opacity-20 ${state.frozen ? 'text-blue-400 bg-gray-700' : 'text-gray-400 hover:text-white hover:bg-gray-700'}`}
                title="Freeze (F)"
              >
                <Snowflake className="w-3 h-3" />
              </button>

              {audioStatus !== 'none' && (
                <button
                  onClick={stopAudio}
                  className="p-1 rounded text-gray-400 hover:text-red-400 hover:bg-gray-700 transition-colors"
                  title="Disconnect audio"
                >
                  <MonitorOff className="w-3 h-3" />
                </button>
              )}

              <div className="w-px h-3 bg-gray-700 mx-0.5" />

              <button
                onClick={() => setModalOpen(true)}
                className="p-1 rounded text-gray-400 hover:text-purple-400 hover:bg-gray-700 transition-colors"
                title="Visualizer Settings"
              >
                <Settings2 className="w-3 h-3" />
              </button>

              <button
                onClick={() => {
                  saveVisualizerDefaults(state);
                  toast.success('Defaults saved');
                }}
                className="p-1 rounded text-gray-400 hover:text-purple-400 hover:bg-gray-700 transition-colors"
                title="Save current settings as default"
              >
                <Save className="w-3 h-3" />
              </button>

              {projectionOpen ? (
                <button
                  onClick={closeProjectionWindow}
                  className="p-1 rounded text-purple-400 bg-purple-400/20 hover:bg-purple-400/30 transition-colors"
                  title="Close Projection Window"
                >
                  <Maximize2 className="w-3 h-3" />
                </button>
              ) : (
                <button
                  onClick={openProjectionWindow}
                  className="p-1 rounded text-gray-400 hover:text-purple-400 hover:bg-gray-700 transition-colors"
                  title="Open Projection Window"
                >
                  <Maximize2 className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>

          {/* Active visualization name */}
          {activeViz && state.running && (
            <div className="px-2.5 pb-1.5 -mt-0.5">
              <span className="text-[9px] text-gray-500 truncate block">{activeViz.name}</span>
            </div>
          )}
        </div>
      </div>

      {/* Full controls modal */}
      {modalOpen && (
        <VisualizerModal
          engine={engineRef.current}
          state={state}
          audioStatus={audioStatus}
          onStartAudio={startAudio}
          onStopAudio={stopAudio}
          onOpenProjection={openProjectionWindow}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
}
